import { AppLanguage, getPreferredLanguage } from '../language';

export interface LabelingBatchOrderI18nTemplate {
  shared: {
    back: string;
    endOrder: string;
    next: string;
  };
  uploadAddress: {
    selectFileFirstAlert: string;
    fileProcessFailedAlert: string;
    templateDownloadFailedAlert: string;
    guidanceTitle: string;
    guidanceDescription: string;
    uploadTitle: string;
    uploadDescription: string;
    uploadAction: string;
    processingAction: string;
    downloadTemplateText: string;
  };
  processAddress: {
    clearOptionLabel: string;
    defaultOptions: string[];
    requiredFieldKeys: string[];
    fieldLabels: Record<string, string>;
    fieldSubtexts: Record<string, string>;
    headerDisplayMap: Record<string, string>;
    requiredAddressFieldsNotMappedError: string;
    requiredAddressFieldsEmptyError: string;
    invalidStatePrefix: string;
    invalidAddressFormat: string;
    addressValidationFailedPrefix: string;
    dangerousGoodsValueError: string;
    signatureValueError: string;
    insuranceValueError: string;
    dataFormatError: string;
    largeFileWarning: string;
    storeResultFailedAlert: string;
    processFileFailedAlert: string;
    templateMappingSuccess: string;
    templateMappingFailed: string;
    templateMappingMissingFieldsTitle: string;
    templateMappingSuggestionsTitle: string;
    acceptedColumnNameIncludes: string;
    templateMappingCheckTemplateHint: string;
    uploadFirstError: string;
    processingData: string;
    resetMappingsSuccess: string;
    title: string;
    description: string;
    applyTemplateMapping: string;
    resetFieldMapping: string;
    tapToSelect: string;
    resetConfirmTitle: string;
    resetConfirmMessage: string;
    resetConfirmText: string;
    cancelText: string;
  };
  confirmBatchAddress: {
    loadDataFailedAlert: string;
    noFailedDataAlert: string;
    rowNumberColumn: string;
    failureReasonColumn: string;
    columnShouldFillPrefix: string;
    failedDataSheetName: string;
    failedAddressFileName: string;
    createFailedDataFileErrorAlert: string;
    friendlyFieldNames: Record<string, string>;
    guidanceTitle: string;
    guidanceDescription: string;
    successTitle: string;
    nextHintEnabled: string;
    nextHintDisabled: string;
    failedCountText: string;
    downloadFailedData: string;
    reupload: string;
  };
}

export const TEMPLATES: Record<AppLanguage, LabelingBatchOrderI18nTemplate> = {
  zh: {
    shared: {
      back: '返回',
      endOrder: '结束下单',
      next: '下一步',
    },
    uploadAddress: {
      selectFileFirstAlert: '请先选择文件',
      fileProcessFailedAlert: '文件处理失败，请确保文件格式正确并包含表头',
      templateDownloadFailedAlert: '下载模板失败，请稍后重试',
      guidanceTitle: '上传收货地址',
      guidanceDescription: '你可以上传任意包含联系人，联系电话，收货地址，货物尺寸，货物重量的表格',
      uploadTitle: '上传文件',
      uploadDescription: '你可以上传任意包含联系人，联系电话，收货地址，货物尺寸，货物重量的表格',
      uploadAction: '上传',
      processingAction: '处理中...',
      downloadTemplateText: '下载模板 >',
    },
    processAddress: {
      clearOptionLabel: '不选择（清除映射）',
      defaultOptions: ['电商单号', '联系人', '手机号', '收货地址', '货物尺寸', '货物重量'],
      requiredFieldKeys: [
        '必填-联系人',
        '必填-手机号',
        '必填-收货地址街道门牌号',
        '必填-收货地址城市',
        '必填-收货地址州',
        '必填-收货地址邮编',
        '必填-货物长度',
        '必填-货物宽度',
        '必填-货物高度',
        '必填-货物重量',
      ],
      fieldLabels: {
        电商订单号: '电商订单号',
        '必填-联系人': '必填-联系人',
        '必填-手机号': '必填-手机号',
        '必填-收货地址街道门牌号': '必填-收货地址街道门牌号',
        '选填-收货地址房间号': '选填-收货地址房间号',
        '必填-收货地址城市': '必填-收货地址城市',
        '必填-收货地址州': '必填-收货地址州',
        '必填-收货地址邮编': '必填-收货地址邮编',
        '必填-货物长度': '必填-货物长度',
        '必填-货物宽度': '必填-货物宽度',
        '必填-货物高度': '必填-货物高度',
        '必填-货物重量': '必填-货物重量',
        '选填-该包裹是危险品或含电池吗': '选填-该包裹是危险品或含电池吗',
        '选填-是否需要购买签名服务': '选填-是否需要购买签名服务',
        '选填-是否需要保险服务': '选填-是否需要保险服务',
      },
      fieldSubtexts: {
        电商订单号: '选填 * 用于匹配订单和快递单',
        '必填-联系人': '必填',
        '必填-手机号': '必填',
        '必填-收货地址街道门牌号': '必填 * 街道地址',
        '选填-收货地址房间号': '选填 * 公寓号/单元号等',
        '必填-收货地址城市': '必填',
        '必填-收货地址州': '必填',
        '必填-收货地址邮编': '必填',
        '必填-货物长度': '必填. 默认单位是英寸',
        '必填-货物宽度': '必填. 默认单位是英寸',
        '必填-货物高度': '必填. 默认单位是英寸',
        '必填-货物重量': '必填. 默认单位是磅',
        '选填-该包裹是危险品或含电池吗': '选填 * 选择是/否',
        '选填-是否需要购买签名服务': '选填 * 选择是/否',
        '选填-是否需要保险服务': '选填 * 如需要请输入金额',
      },
      headerDisplayMap: {
        电商订单号: '电商订单号',
        订单号: '订单号',
        单号: '单号',
        电商单号: '电商单号',
        '必填-联系人': '必填-联系人',
        联系人: '联系人',
        收件人: '收件人',
        姓名: '姓名',
        '必填-手机号': '必填-手机号',
        手机号: '手机号',
        电话: '电话',
        联系电话: '联系电话',
        '必填-收货地址街道门牌号': '必填-收货地址街道门牌号',
        收货地址街道门牌号: '收货地址街道门牌号',
        街道地址: '街道地址',
        地址: '地址',
        '选填-收货地址房间号': '选填-收货地址房间号',
        收货地址房间号: '收货地址房间号',
        房间号: '房间号',
        单元号: '单元号',
        '必填-收货地址城市': '必填-收货地址城市',
        收货地址城市: '收货地址城市',
        城市: '城市',
        '必填-收货地址州': '必填-收货地址州',
        收货地址州: '收货地址州',
        州: '州',
        '必填-收货地址邮编': '必填-收货地址邮编',
        收货地址邮编: '收货地址邮编',
        邮编: '邮编',
        '必填-货物长度': '必填-货物长度',
        '必填-货物长度(英寸)': '必填-货物长度(英寸)',
        货物长度: '货物长度',
        长度: '长度',
        '必填-货物长度(in)': '必填-货物长度(in)',
        '必填-货物长度（英寸）': '必填-货物长度（英寸）',
        '必填-货物宽度': '必填-货物宽度',
        '必填-货物宽度(英寸)': '必填-货物宽度(英寸)',
        货物宽度: '货物宽度',
        宽度: '宽度',
        '必填-货物宽度(in)': '必填-货物宽度(in)',
        '必填-货物宽度（英寸）': '必填-货物宽度（英寸）',
        '必填-货物高度': '必填-货物高度',
        '必填-货物高度(英寸)': '必填-货物高度(英寸)',
        货物高度: '货物高度',
        高度: '高度',
        '必填-货物高度(in)': '必填-货物高度(in)',
        '必填-货物高度（英寸）': '必填-货物高度（英寸）',
        '必填-货物重量': '必填-货物重量',
        '必填-货物重量(磅)': '必填-货物重量(磅)',
        货物重量: '货物重量',
        重量: '重量',
        '必填-货物重量(lb)': '必填-货物重量(lb)',
        '必填-货物重量（磅）': '必填-货物重量（磅）',
        '选填-该包裹是危险品或含电池吗': '选填-该包裹是危险品或含电池吗',
        危险品: '危险品',
        含电池: '含电池',
        '选填-是否需要购买签名服务': '选填-是否需要购买签名服务',
        签名服务: '签名服务',
        签名: '签名',
        '选填-是否需要保险服务': '选填-是否需要保险服务',
        保险服务: '保险服务',
        保险: '保险',
      },
      requiredAddressFieldsNotMappedError: '必填的地址字段未完全映射',
      requiredAddressFieldsEmptyError: '必填的地址字段不能为空',
      invalidStatePrefix: '州名称不正确',
      invalidAddressFormat: '地址格式不正确',
      addressValidationFailedPrefix: '地址验证失败',
      dangerousGoodsValueError: '危险品字段必须填写"危险品"或"含电池商品"',
      signatureValueError: '签名服务选项不正确，可选值：普通签名、成人签名',
      insuranceValueError: '保险金额格式不正确，请输入有效的数字金额',
      dataFormatError: '数据格式错误',
      largeFileWarning:
        '您上传的文件较大，处理时间可能较长。建议将文件拆分为多个小文件（每个不超过1000行）进行上传。\n\n如需批量处理大文件，请联系客服获取支持。',
      storeResultFailedAlert: '存储处理结果失败，请重试',
      processFileFailedAlert: '处理文件时出现错误，请稍后重试',
      templateMappingSuccess: '模板映射成功！已自动匹配 {{mapped}}/{{total}} 个字段。',
      templateMappingFailed: '模板映射失败！',
      templateMappingMissingFieldsTitle: '缺少以下必需字段：',
      templateMappingSuggestionsTitle: '建议的列名：',
      acceptedColumnNameIncludes: '可接受的列名包括',
      templateMappingCheckTemplateHint: '请检查您的文件列名是否与模板一致。',
      uploadFirstError: '请先上传文件。',
      processingData: '正在处理数据...',
      resetMappingsSuccess: '已重置所有字段映射',
      title: '处理地址',
      description: '卡片左侧是快递下单所需字段，右侧是你上传的表格字段，轻点卡片将它们对应起来。',
      applyTemplateMapping: '使用模板映射',
      resetFieldMapping: '重新匹配字段',
      tapToSelect: '轻点选择',
      resetConfirmTitle: '确认重置',
      resetConfirmMessage: '确定要重置所有字段映射吗？此操作不可逆。',
      resetConfirmText: '确认重置',
      cancelText: '取消',
    },
    confirmBatchAddress: {
      loadDataFailedAlert: '数据加载失败，请重新上传',
      noFailedDataAlert: '没有失败数据',
      rowNumberColumn: '行号',
      failureReasonColumn: '失败原因',
      columnShouldFillPrefix: '该列应填写：',
      failedDataSheetName: '失败数据',
      failedAddressFileName: '失败地址数据.xlsx',
      createFailedDataFileErrorAlert: '创建失败数据文件时出错',
      friendlyFieldNames: {
        电商订单号: '订单号',
        联系人: '收件人姓名',
        手机号: '联系电话',
        收货地址: '配送地址',
        货物长度: '包裹长度',
        货物宽度: '包裹宽度',
        货物高度: '包裹高度',
        货物重量: '包裹重量',
      },
      guidanceTitle: '批量上传结果',
      guidanceDescription: '共{{total}}条数据，{{success}}条处理成功',
      successTitle: '上传成功{{success}}条',
      nextHintEnabled: '点击下一步预览信息和价格',
      nextHintDisabled: '请返回修改映射或重新上传',
      failedCountText: '{{count}}条数据处理失败',
      downloadFailedData: '下载失败数据',
      reupload: '重新上传',
    },
  },
  en: {
    shared: {
      back: 'Back',
      endOrder: 'End Order',
      next: 'Next',
    },
    uploadAddress: {
      selectFileFirstAlert: 'Please select a file first.',
      fileProcessFailedAlert: 'File processing failed. Make sure the file has headers and valid format.',
      templateDownloadFailedAlert: 'Failed to download template. Please try again later.',
      guidanceTitle: 'Upload Recipient Addresses',
      guidanceDescription:
        'Upload any spreadsheet that includes recipient, phone, address, package dimensions, and weight.',
      uploadTitle: 'Upload File',
      uploadDescription:
        'Upload any spreadsheet that includes recipient, phone, address, package dimensions, and weight.',
      uploadAction: 'Upload',
      processingAction: 'Processing...',
      downloadTemplateText: 'Download Template >',
    },
    processAddress: {
      clearOptionLabel: 'No Selection (Clear Mapping)',
      defaultOptions: ['Order Number', 'Recipient', 'Phone', 'Address', 'Dimensions', 'Weight'],
      requiredFieldKeys: [
        '必填-联系人',
        '必填-手机号',
        '必填-收货地址街道门牌号',
        '必填-收货地址城市',
        '必填-收货地址州',
        '必填-收货地址邮编',
        '必填-货物长度',
        '必填-货物宽度',
        '必填-货物高度',
        '必填-货物重量',
      ],
      fieldLabels: {
        电商订单号: 'E-commerce Order Number',
        '必填-联系人': 'Required - Recipient',
        '必填-手机号': 'Required - Phone',
        '必填-收货地址街道门牌号': 'Required - Street Address',
        '选填-收货地址房间号': 'Optional - Apt/Unit',
        '必填-收货地址城市': 'Required - City',
        '必填-收货地址州': 'Required - State',
        '必填-收货地址邮编': 'Required - ZIP Code',
        '必填-货物长度': 'Required - Package Length',
        '必填-货物宽度': 'Required - Package Width',
        '必填-货物高度': 'Required - Package Height',
        '必填-货物重量': 'Required - Package Weight',
        '选填-该包裹是危险品或含电池吗': 'Optional - Dangerous Goods or Battery',
        '选填-是否需要购买签名服务': 'Optional - Signature Service',
        '选填-是否需要保险服务': 'Optional - Insurance',
      },
      fieldSubtexts: {
        电商订单号: 'Optional * For matching order and shipping label',
        '必填-联系人': 'Required',
        '必填-手机号': 'Required',
        '必填-收货地址街道门牌号': 'Required * Street address',
        '选填-收货地址房间号': 'Optional * Apt / Unit',
        '必填-收货地址城市': 'Required',
        '必填-收货地址州': 'Required',
        '必填-收货地址邮编': 'Required',
        '必填-货物长度': 'Required. Default unit is inch.',
        '必填-货物宽度': 'Required. Default unit is inch.',
        '必填-货物高度': 'Required. Default unit is inch.',
        '必填-货物重量': 'Required. Default unit is lb.',
        '选填-该包裹是危险品或含电池吗': 'Optional * Yes/No',
        '选填-是否需要购买签名服务': 'Optional * Yes/No',
        '选填-是否需要保险服务': 'Optional * Enter amount if needed',
      },
      headerDisplayMap: {
        电商订单号: 'E-commerce Order Number',
        订单号: 'Order Number',
        单号: 'Order ID',
        电商单号: 'E-commerce Order ID',
        '必填-联系人': 'Required - Recipient',
        联系人: 'Recipient',
        收件人: 'Receiver',
        姓名: 'Name',
        '必填-手机号': 'Required - Phone',
        手机号: 'Phone',
        电话: 'Phone',
        联系电话: 'Contact Phone',
        '必填-收货地址街道门牌号': 'Required - Street Address',
        收货地址街道门牌号: 'Street Address',
        街道地址: 'Street Address',
        地址: 'Address',
        '选填-收货地址房间号': 'Optional - Apt/Unit',
        收货地址房间号: 'Apt/Unit',
        房间号: 'Room',
        单元号: 'Unit',
        '必填-收货地址城市': 'Required - City',
        收货地址城市: 'City',
        城市: 'City',
        '必填-收货地址州': 'Required - State',
        收货地址州: 'State',
        州: 'State',
        '必填-收货地址邮编': 'Required - ZIP Code',
        收货地址邮编: 'ZIP Code',
        邮编: 'ZIP Code',
        '必填-货物长度': 'Required - Package Length',
        '必填-货物长度(英寸)': 'Required - Package Length (in)',
        货物长度: 'Package Length',
        长度: 'Length',
        '必填-货物长度(in)': 'Required - Package Length (in)',
        '必填-货物长度（英寸）': 'Required - Package Length (in)',
        '必填-货物宽度': 'Required - Package Width',
        '必填-货物宽度(英寸)': 'Required - Package Width (in)',
        货物宽度: 'Package Width',
        宽度: 'Width',
        '必填-货物宽度(in)': 'Required - Package Width (in)',
        '必填-货物宽度（英寸）': 'Required - Package Width (in)',
        '必填-货物高度': 'Required - Package Height',
        '必填-货物高度(英寸)': 'Required - Package Height (in)',
        货物高度: 'Package Height',
        高度: 'Height',
        '必填-货物高度(in)': 'Required - Package Height (in)',
        '必填-货物高度（英寸）': 'Required - Package Height (in)',
        '必填-货物重量': 'Required - Package Weight',
        '必填-货物重量(磅)': 'Required - Package Weight (lb)',
        货物重量: 'Package Weight',
        重量: 'Weight',
        '必填-货物重量(lb)': 'Required - Package Weight (lb)',
        '必填-货物重量（磅）': 'Required - Package Weight (lb)',
        '选填-该包裹是危险品或含电池吗': 'Optional - Dangerous Goods or Battery',
        危险品: 'Dangerous Goods',
        含电池: 'Battery Included',
        '选填-是否需要购买签名服务': 'Optional - Signature Service',
        签名服务: 'Signature Service',
        签名: 'Signature',
        '选填-是否需要保险服务': 'Optional - Insurance',
        保险服务: 'Insurance Service',
        保险: 'Insurance',
      },
      requiredAddressFieldsNotMappedError: 'Required address fields are not fully mapped.',
      requiredAddressFieldsEmptyError: 'Required address fields cannot be empty.',
      invalidStatePrefix: 'Invalid state',
      invalidAddressFormat: 'Invalid address format',
      addressValidationFailedPrefix: 'Address validation failed',
      dangerousGoodsValueError:
        'Dangerous goods column must be "危险品" or "含电池商品".',
      signatureValueError: 'Signature value must be "普通签名" or "成人签名".',
      insuranceValueError: 'Invalid insurance amount. Enter a valid number.',
      dataFormatError: 'Invalid data format',
      largeFileWarning:
        'Your file is large and may take longer to process. We recommend splitting it into smaller files (up to 1000 rows each).\n\nFor larger batch processing support, please contact customer service.',
      storeResultFailedAlert: 'Failed to save processing result. Please try again.',
      processFileFailedAlert: 'An error occurred while processing the file. Please try again later.',
      templateMappingSuccess: 'Template mapping succeeded. Auto-matched {{mapped}}/{{total}} fields.',
      templateMappingFailed: 'Template mapping failed.',
      templateMappingMissingFieldsTitle: 'Missing required fields:',
      templateMappingSuggestionsTitle: 'Suggested column names:',
      acceptedColumnNameIncludes: 'Accepted column names include',
      templateMappingCheckTemplateHint: 'Please check whether your column names match the template.',
      uploadFirstError: 'Please upload a file first.',
      processingData: 'Processing data...',
      resetMappingsSuccess: 'All field mappings have been reset.',
      title: 'Process Addresses',
      description:
        'Left side shows required shipping fields, right side shows your uploaded columns. Tap a card to map them.',
      applyTemplateMapping: 'Apply Template Mapping',
      resetFieldMapping: 'Reset Field Mapping',
      tapToSelect: 'Tap to Select',
      resetConfirmTitle: 'Confirm Reset',
      resetConfirmMessage: 'Reset all field mappings? This action cannot be undone.',
      resetConfirmText: 'Reset',
      cancelText: 'Cancel',
    },
    confirmBatchAddress: {
      loadDataFailedAlert: 'Failed to load data. Please re-upload.',
      noFailedDataAlert: 'No failed rows.',
      rowNumberColumn: 'Row Number',
      failureReasonColumn: 'Failure Reason',
      columnShouldFillPrefix: 'Expected Field: ',
      failedDataSheetName: 'Failed Rows',
      failedAddressFileName: 'failed_addresses.xlsx',
      createFailedDataFileErrorAlert: 'Failed to generate failed data file.',
      friendlyFieldNames: {
        电商订单号: 'Order Number',
        联系人: 'Recipient Name',
        手机号: 'Phone Number',
        收货地址: 'Shipping Address',
        货物长度: 'Package Length',
        货物宽度: 'Package Width',
        货物高度: 'Package Height',
        货物重量: 'Package Weight',
      },
      guidanceTitle: 'Batch Upload Result',
      guidanceDescription: '{{total}} rows total, {{success}} rows processed successfully',
      successTitle: '{{success}} rows uploaded successfully',
      nextHintEnabled: 'Click next to preview details and rates',
      nextHintDisabled: 'Go back to fix mappings or re-upload',
      failedCountText: '{{count}} rows failed',
      downloadFailedData: 'Download Failed Rows',
      reupload: 'Re-upload',
    },
  },
};

export const LABELING_BATCH_ORDER_NAMESPACE = 'labelingBatchOrder';

export const LABELING_BATCH_ORDER_RESOURCE_KEY = 'template';

export const LABELING_BATCH_ORDER_I18N_RESOURCES: Record<
  AppLanguage,
  Record<typeof LABELING_BATCH_ORDER_RESOURCE_KEY, LabelingBatchOrderI18nTemplate>
> = {
  zh: {
    [LABELING_BATCH_ORDER_RESOURCE_KEY]: TEMPLATES.zh,
  },
  en: {
    [LABELING_BATCH_ORDER_RESOURCE_KEY]: TEMPLATES.en,
  },
};

export const getLabelingBatchOrderTemplate = (
  language: AppLanguage = getPreferredLanguage(),
): LabelingBatchOrderI18nTemplate => TEMPLATES[language] || TEMPLATES.zh;
